import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAdEligibility } from "@/hooks/useAdEligibility";
import {
  ADSENSE_CLIENT_ID,
  ADSENSE_SCRIPT_ID,
  isAdSenseConfigured,
  isAdvertisingSupportedPath,
} from "@/lib/adsense";

/**
 * Loads Auto ads only after authentication and subscription entitlements are
 * resolved. The static script must not live in index.html because that would
 * also load Google advertising for Plus and Pro subscribers.
 */
export function AdSenseLoader() {
  const { isResolved, shouldShowAds } = useAdEligibility();
  const { pathname } = useLocation();
  const isSupportedPath = isAdvertisingSupportedPath(pathname);

  useEffect(() => {
    if (!isResolved || !import.meta.env.PROD || !isAdSenseConfigured()) return;

    const existingScript = document.getElementById(ADSENSE_SCRIPT_ID);

    if (!shouldShowAds || !isSupportedPath) {
      existingScript?.remove();
      document
        .querySelectorAll("ins.adsbygoogle-noablate, .google-auto-placed")
        .forEach((element) => element.remove());
      return;
    }

    if (existingScript) return;

    const script = document.createElement("script");
    script.id = ADSENSE_SCRIPT_ID;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(ADSENSE_CLIENT_ID)}`;
    script.dataset.adClient = ADSENSE_CLIENT_ID;
    script.addEventListener("error", () => script.remove(), { once: true });
    document.head.appendChild(script);
  }, [isResolved, isSupportedPath, shouldShowAds]);

  return null;
}
